import { createContext, ReactNode, type PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import type { IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, ButtonProps, Text, TextProps } from '@frontend/design-system';

interface HeaderContextValue {
  size?: 'sm' | 'md' | 'lg';
}

const HeaderContext = createContext<HeaderContextValue>({});

const headerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${theme.spacing(2)};
`;

interface HeaderProps extends PropsWithChildren<React.HTMLAttributes<HTMLElement>> {
  size?: 'sm' | 'md' | 'lg';
  innerRef?: React.Ref<HTMLElement>;
}

export function Root({ children, className, size = 'md', innerRef, ...rest }: HeaderProps) {
  return (
    <HeaderContext.Provider value={{ size }}>
      <header ref={innerRef} css={headerStyles} className={className} {...rest}>
        {children}
      </header>
    </HeaderContext.Provider>
  );
}

const sectionTitleStyles = css`
  margin-bottom: -${theme.spacing(0.75)};
`;

type SlotProps = PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>;

export function SectionTitle({ children, ...rest }: TextProps) {
  return (
    <Text css={sectionTitleStyles} {...rest} size='large'>
      {children}
    </Text>
  );
}

const titleRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${theme.spacing(1)};
  min-height: 40px;
  margin-bottom: ${theme.spacing(0.75)};

  @media (min-width: ${breakpoints.large.min}) {
    min-height: 48px;
    gap: ${theme.spacing(2)};
  }
`;

export function TitleRow({ children, ...rest }: SlotProps) {
  return (
    <div css={titleRowStyles} {...rest}>
      {children}
    </div>
  );
}

const titleContainerStyles = css`
  display: flex;
  gap: ${theme.spacing(1)};
  flex: 1;
  padding-top: 7px;
  padding-bottom: 7px;
  min-width: 0;
`;

interface TitleProps {
  children: ReactNode;
}

export function Title({ children, ...rest }: SlotProps) {
  return (
    <div css={titleContainerStyles} {...rest}>
      {children}
    </div>
  );
}

const actionsContainerStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-shrink: 0;
`;

export function Actions({ children, ...rest }: SlotProps) {
  return (
    <div css={actionsContainerStyles} {...rest}>
      {children}
    </div>
  );
}

interface SecondaryActionProps {
  children: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  className?: string;
}

export function SecondaryAction({ onClick, ...rest }: ButtonProps) {
  return (
    <Button
      css={css`
        border: 1px solid ${theme.colors.neutral70};
        width: 40px;
        height: 40px;
        padding: 0;
      `}
      size='large'
      variant='secondary'
      {...rest}
    />
  );
}

interface PrimaryActionProps {
  children?: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  className?: string;
}

export function PrimaryAction({ children, ...rest }: ButtonProps) {
  return (
    <Button size='large' {...rest}>
      {children}
    </Button>
  );
}

const controlsRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-bottom: ${theme.spacing(1.25)};

  @media (min-width: ${breakpoints.large.min}) {
    gap: ${theme.spacing(1)};
  }
`;

export function Controls({ children, ...rest }: SlotProps) {
  return (
    <div css={controlsRowStyles} {...rest}>
      {children}
    </div>
  );
}

export function ControlButton(props: ButtonProps) {
  return (
    <Button
      variant='secondary'
      size='medium'
      css={css`
        width: 36px;
        height: 36px;
        padding: 0;
      `}
      {...props}
    />
  );
}

const tabsContainerStyles = css`
  margin-left: -${theme.spacing(2)};
  margin-right: -${theme.spacing(2)};
`;

export function Tabs({ children, ...rest }: SlotProps) {
  return (
    <div css={tabsContainerStyles} {...rest}>
      {children}
    </div>
  );
}

const filtersRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
  flex-wrap: wrap;

  @media (min-width: ${breakpoints.large.min}) {
    gap: ${theme.spacing(3)};
  }
`;

export function Filters({ children, ...rest }: SlotProps) {
  return (
    <div css={filtersRowStyles} {...rest}>
      {children}
    </div>
  );
}

const quickFiltersStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};
`;

export function QuickFilters({ children, ...rest }: SlotProps) {
  return (
    <div css={quickFiltersStyles} {...rest}>
      {children}
    </div>
  );
}

const inlineRowStyles = css`
  display: flex;
  flex-direction: column-reverse;
  gap: ${theme.spacing(1.5)};
  margin-bottom: ${theme.spacing(2)};

  @container (min-width: ${breakpoints.small.min}px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export function InlineRow({ children, ...rest }: SlotProps) {
  return (
    <div css={inlineRowStyles} {...rest}>
      {children}
    </div>
  );
}

const inlineControlsStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

export function InlineControls({ children, ...rest }: SlotProps) {
  return (
    <div css={inlineControlsStyles} {...rest}>
      {children}
    </div>
  );
}

export type { HeaderProps, SlotProps, TitleProps, PrimaryActionProps, SecondaryActionProps };
